import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { ReleaseNote } from '../types/releaseNotes';
import { format } from 'date-fns';

interface ReleaseNotesModalProps {
    open: boolean;
    onClose: () => void;
    releaseNotes: ReleaseNote[];
}

const TYPE_STYLES: Record<string, { color: string; bg: string }> = {
    feature:     { color: '#4fc3f7', bg: 'rgba(79,195,247,0.12)' },
    bugfix:      { color: '#ef9a9a', bg: 'rgba(239,154,154,0.12)' },
    improvement: { color: '#a5d6a7', bg: 'rgba(165,214,167,0.12)' },
};

function TypeTag({ type }: { type: string | null }) {
    if (!type) return <Box sx={{ width: 92, flexShrink: 0 }} />;
    const s = TYPE_STYLES[type] ?? { color: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.07)' };
    const label = type.charAt(0).toUpperCase() + type.slice(1);
    return (
        <Box sx={{
            width: 92,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1,
            py: 0.3,
            borderRadius: '4px',
            bgcolor: s.bg,
            border: `1px solid ${s.color}22`,
        }}>
            <Typography sx={{
                fontSize: '0.68rem',
                fontWeight: 600,
                color: s.color,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
            }}>
                {label}
            </Typography>
        </Box>
    );
}

export default function ReleaseNotesModal({ open, onClose, releaseNotes }: ReleaseNotesModalProps) {
    const groupKeys: string[] = [];
    const groups: Record<string, ReleaseNote[]> = {};
    for (const note of releaseNotes) {
        const key = note.release_version ?? '__unreleased__';
        if (!groups[key]) {
            groupKeys.push(key);
            groups[key] = [];
        }
        groups[key].push(note);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: '#161c23',
                        color: 'white',
                        borderRadius: 2.5,
                        boxShadow: '0px 16px 48px rgba(0,0,0,0.7)',
                        border: '1px solid rgba(255,255,255,0.06)',
                    }
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, pt: 2.5, px: 3 }}>
                <NewReleasesIcon sx={{ color: '#4fc3f7', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', flexGrow: 1, letterSpacing: '-0.01em' }}>
                    What's New
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)' }, '&:focus': { outline: 'none' } }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </DialogTitle>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogContent sx={{ px: 3, py: 2.5, maxHeight: 480, overflowY: 'auto' }}>
                {groupKeys.length === 0 ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 4, fontSize: '0.875rem' }}>
                        No release notes available.
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {groupKeys.map((key, groupIndex) => {
                            const notes = groups[key];
                            const isUnreleased = key === '__unreleased__';
                            return (
                                <Box key={key}>
                                    {/* Version header */}
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                                        <Typography sx={{
                                            fontWeight: 700,
                                            fontSize: '0.875rem',
                                            color: isUnreleased
                                                ? '#ffd54f'
                                                : groupIndex === 0 ? '#4fc3f7' : 'rgba(255,255,255,0.55)',
                                        }}>
                                            {isUnreleased ? 'Unreleased' : `v${key}`}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.28)' }}>
                                            {format(new Date(notes[0].published_at), 'dd MMM yyyy')}
                                        </Typography>
                                    </Box>

                                    {/* Notes */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {notes.map((note) => (
                                            <Box key={note.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <TypeTag type={note.type} />
                                                <Typography variant="body2" sx={{
                                                    color: 'rgba(255,255,255,0.75)',
                                                    fontSize: '0.825rem',
                                                    lineHeight: 1.55,
                                                    pt: '2px',
                                                }}>
                                                    {note.content}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>

                                    {groupIndex < groupKeys.length - 1 && (
                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mt: 3 }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    disableElevation
                    sx={{
                        bgcolor: '#4fc3f7',
                        color: '#161c23',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        letterSpacing: '0.04em',
                        px: 3,
                        '&:hover': { bgcolor: '#81d4fa' },
                    }}
                >
                    Got it
                </Button>
            </DialogActions>
        </Dialog>
    );
}
